import { computed, ref } from "vue"
import { defineStore } from "pinia"
import { UseRootStore } from "@/model/RootStore"
import { ExtendedAccessStatus, UsageFeature, type ExtendedAccess } from "@/types/extendedAccess"
import * as apiClient from "@/utils/ApiClient"

const MaxTimeoutMilliseconds = 2_147_483_647
const MillisecondsPerDay = 86_400_000

export const useExtendedAccessStore = defineStore("extendedAccess", () => {
	const rootStore = UseRootStore()
	const extendedAccess = ref<ExtendedAccess | null>(null)
	const isStarting = ref(false)
	let loadedForToken: string | null = null
	let accessEndTimeout: ReturnType<typeof setTimeout> | null = null

	const canStartExtendedAccess = computed(() => extendedAccess.value?.status === ExtendedAccessStatus.Available)
	const parcelReportQuota = computed(
		() => extendedAccess.value?.usageQuotas.find(quota => quota.feature === UsageFeature.ParcelReport) ?? null
	)
	const daysLeft = computed(() => {
		const endsAt = extendedAccess.value?.endsAt
		return endsAt ? Math.max(Math.ceil((new Date(endsAt).getTime() - Date.now()) / MillisecondsPerDay), 0) : 0
	})

	async function load({ force = false }: { force?: boolean } = {}): Promise<void> {
		if (!rootStore.isUserLoggedIn) {
			extendedAccess.value = null
			loadedForToken = null
			return
		}
		if (!force && loadedForToken === rootStore.userApiToken) return

		loadedForToken = rootStore.userApiToken
		try {
			extendedAccess.value = await apiClient.fetchExtendedAccess(rootStore.userApiToken)
			scheduleRefreshAtEnd(extendedAccess.value)
		} catch (error) {
			console.error("Failed to fetch extended access:", error)
			loadedForToken = null
		}
	}

	async function refreshAccess(): Promise<void> {
		if (await rootStore.refreshUserApiTokenIfNeeded({ force: true })) {
			await load()
		}
	}

	function scheduleRefreshAtEnd(access: ExtendedAccess): void {
		if (accessEndTimeout) clearTimeout(accessEndTimeout)
		accessEndTimeout = null
		if (access.status !== ExtendedAccessStatus.Active || !access.endsAt) return

		const millisecondsUntilEnd = new Date(access.endsAt).getTime() - Date.now()
		if (millisecondsUntilEnd < MaxTimeoutMilliseconds) {
			accessEndTimeout = setTimeout(refreshAccess, Math.max(millisecondsUntilEnd, 0))
		}
	}

	async function start(): Promise<boolean> {
		isStarting.value = true
		try {
			await apiClient.updateCurrentUser(rootStore.userApiToken, { extendedAccess: true })
			await rootStore.refreshUserApiTokenIfNeeded({ force: true })
			await load({ force: true })
			return true
		} catch (error) {
			console.error("Failed to start extended access:", error)
			return false
		} finally {
			isStarting.value = false
		}
	}

	return {
		extendedAccess,
		isStarting,
		canStartExtendedAccess,
		parcelReportQuota,
		daysLeft,
		load,
		start,
	}
})
